import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Select,
  Avatar,
  Spinner,
  useColorModeValue,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Stack,
} from "@chakra-ui/react";
import { FaUserPlus, FaRss, FaEnvelope } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { ROLES, role as getRole, statutMembre } from "variables/veille";
import { supabase } from "lib/supabase";
import { useSession } from "contexts/SessionContext";
import ProfileHeader from "components/ProfileHeader";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Equipe() {
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const { org, profile, role: monRole, isAdmin, isManager, isSuperAdmin } = useSession();
  const orgId = org && org.id;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(true);
  const [membres, setMembres] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [nbVeilles, setNbVeilles] = useState(0);
  const [accesParUser, setAccesParUser] = useState({}); // userId -> count

  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("analyste");

  const charger = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const [mRes, iRes, vRes, aRes] = await Promise.all([
      supabase.from("org_members").select("user_id, role, statut, created_at, profiles(full_name, email, avatar_url)").eq("organisation_id", orgId).order("created_at"),
      supabase.from("invitations").select("id, email, role, created_at").eq("organisation_id", orgId).eq("statut", "envoyee").order("created_at", { ascending: false }),
      supabase.from("veilles").select("id", { count: "exact", head: true }).eq("organisation_id", orgId),
      supabase.from("veille_access").select("user_id"),
    ]);
    setMembres(mRes.data || []);
    setInvitations(iRes.data || []);
    setNbVeilles(vRes.count || 0);
    const counts = {};
    (aRes.data || []).forEach((a) => { counts[a.user_id] = (counts[a.user_id] || 0) + 1; });
    setAccesParUser(counts);
    setLoading(false);
  }, [orgId]);

  useEffect(() => { charger(); }, [charger]);

  const nomMembre = (m) => (m.profiles && (m.profiles.full_name || m.profiles.email)) || m.user_id;
  const veillesDe = (m) => (m.role === "admin" || m.role === "manager" || m.role === "super_admin") ? nbVeilles : (accesParUser[m.user_id] || 0);

  async function changerRole(userId, r) {
    setMembres((p) => p.map((m) => (m.user_id === userId ? { ...m, role: r } : m)));
    const { error } = await supabase.from("org_members").update({ role: r }).eq("organisation_id", orgId).eq("user_id", userId);
    if (error) { toast({ title: "Changement de rôle impossible", description: error.message, status: "error", duration: 3000 }); charger(); }
    else toast({ title: "Rôle mis à jour", description: (getRole(r) || {}).label, status: "success", duration: 2000 });
  }

  async function inviter() {
    if (!EMAIL_RE.test(email)) return toast({ title: "Adresse e-mail invalide.", status: "warning", duration: 2500 });
    const { error } = await supabase.from("invitations").insert({ organisation_id: orgId, email: email.trim(), role: roleId, invited_by: profile && profile.id });
    if (error) return toast({ title: "Invitation impossible", description: error.message, status: "error", duration: 3000 });
    toast({ title: "Invitation envoyée", description: email, status: "success", duration: 3000 });
    setPrenom(""); setEmail(""); onClose(); charger();
  }

  const utilises = membres.filter((m) => m.statut !== "suspendu").length;
  const autres = membres.filter((m) => !(profile && m.user_id === profile.id));
  const sieges = (org && org.sieges) || 8;
  const monNom = (profile && (profile.full_name || profile.email)) || "";

  if (loading) {
    return <Flex direction="column" pt={{ base: "120px", md: "75px" }} justify="center" align="center" py="60px"><Spinner color="teal.300" size="lg" /></Flex>;
  }

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête de page */}
      <Flex align="center" mb="28px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Équipe</Text>
          <Text fontSize="md" color="gray.700" mt="4px">Votre profil et les personnes qui travaillent sur vos veilles.</Text>
        </Box>
      </Flex>

      {/* Votre profil */}
      <ProfileHeader
        stats={[
          { value: nbVeilles, label: "veilles" },
          { value: `${utilises}/${sieges}`, label: "sièges" },
          { value: autres.length, label: "membres" },
        ]}
      />

      {/* Membres — rubrique grise */}
      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "22px" }}>
        <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap="12px" mb="18px" px="4px">
          <Flex align="center">
            <Box w="6px" h="42px" borderRadius="full" bg="teal.300" me="14px" flexShrink={0} />
            <Box>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>Votre équipe</Text>
              <Text fontSize="sm" color="gray.700">Les personnes qui travaillent sur vos veilles.</Text>
            </Box>
          </Flex>
          {isManager && (
            <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaUserPlus} />} onClick={onOpen}>Inviter</Button>
          )}
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="16px">
          {membres.map((m) => {
            const moi = profile && m.user_id === profile.id;
            return (
              <Box key={m.user_id} bg="white" borderRadius="16px" p="20px" boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                <Flex direction="column" w="100%">
                  <Flex align="center" mb="16px">
                    <Avatar src={m.profiles && m.profiles.avatar_url} name={nomMembre(m)} w="52px" h="52px" me="12px" />
                    <Box flex="1" minW="0">
                      <Flex align="center" gap="6px">
                        <Text fontSize="md" fontWeight="bold" color="gray.800" isTruncated>{nomMembre(m)}</Text>
                        {moi && <Badge colorScheme="teal" borderRadius="6px" fontSize="9px">Vous</Badge>}
                      </Flex>
                      <Text fontSize="xs" color="gray.500" isTruncated>{m.profiles && m.profiles.email}</Text>
                    </Box>
                    <Badge colorScheme={statutMembre(m.statut).color} borderRadius="8px">{statutMembre(m.statut).label}</Badge>
                  </Flex>
                  <Flex align="center" justify="space-between" gap="10px">
                    {/* Un super admin n'est modifiable que par un super admin ; un admin normal
                        ne peut pas non plus attribuer le rôle super admin (verrou aussi côté base). */}
                    <Select size="sm" value={m.role} onChange={(e) => changerRole(m.user_id, e.target.value)} borderRadius="10px" isDisabled={!isAdmin || moi || (m.role === "super_admin" && !isSuperAdmin)}>
                      {ROLES.filter((r) => {
                        if (r.id === "admin" || r.id === "analyste") return true;
                        if (r.id === "super_admin") return isSuperAdmin || m.role === "super_admin";
                        return false;
                      }).map((r) => (<option key={r.id} value={r.id}>{r.label}</option>))}
                    </Select>
                    <Flex align="center" color="gray.500" fontSize="xs" whiteSpace="nowrap"><Icon as={FaRss} me="4px" />{veillesDe(m)} veilles</Flex>
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </SimpleGrid>

        {invitations.length > 0 && (
          <Box mt="22px">
            <Text fontSize="sm" fontWeight="bold" color="gray.700" mb="10px" px="4px">Invitations en attente</Text>
            <Flex direction="column" gap="10px">
              {invitations.map((inv) => (
                <Flex key={inv.id} align="center" bg="white" borderRadius="12px" px="16px" py="12px" gap="12px" boxShadow="0 2px 6px rgba(20,20,43,0.04)">
                  <Flex w="38px" h="38px" borderRadius="10px" bg="teal.50" align="center" justify="center"><Icon as={FaEnvelope} color="teal.500" fontSize="14px" /></Flex>
                  <Box flex="1" minW="0">
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" isTruncated>{inv.email}</Text>
                    <Text fontSize="xs" color="gray.500">Rôle proposé : {(getRole(inv.role) || {}).label}</Text>
                  </Box>
                  <Badge colorScheme="blue" borderRadius="6px">Invité</Badge>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Box>

      {/* Modal invitation */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="15px">
          <ModalHeader>Inviter un membre</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="14px">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                <FormControl><FormLabel fontSize="sm" fontWeight="bold">Prénom (optionnel)</FormLabel><Input value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Alex" borderRadius="12px" fontSize="sm" /></FormControl>
                <FormControl><FormLabel fontSize="sm" fontWeight="bold">E-mail</FormLabel><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@entreprise.fr" borderRadius="12px" fontSize="sm" /></FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">Rôle</FormLabel>
                <Select value={roleId} onChange={(e) => setRoleId(e.target.value)} borderRadius="12px" fontSize="sm">
                  {ROLES.filter((r) => r.id === "admin" || r.id === "analyste").map((r) => (<option key={r.id} value={r.id}>{r.label} — {r.detail}</option>))}
                </Select>
              </FormControl>
              <Box bg="gray.50" borderRadius="12px" p="12px">
                <Text fontSize="xs" color="gray.600">Une invitation par e-mail sera enregistrée. Les accès fins aux veilles se règlent depuis <Text as="span" fontWeight="bold" color="gray.800">Administration</Text> une fois l'invitation acceptée.</Text>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" borderRadius="12px" mr="10px" onClick={onClose}>Annuler</Button>
            <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaUserPlus} />} onClick={inviter}>Envoyer l'invitation</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
